import { CardShell, SelectField } from '@atlas/ui-components'
import { Button, Chip, Input, Switch } from '@atlas/ui-primitives'
import { TypeSpecificControls } from '@/components/prompts/PromptTypeSpecificControls'
import type { PromptListRecord, PromptType, PromptVariable } from '@/domain/prompt.types'
import { PROMPT_VARIABLE_TYPE_LABEL, PromptVariableType, variableTypesFor } from '@/domain/prompt.types'

export interface PromptVariablePanelProps {
	variables: PromptVariable[]
	/** Shared lists a `list`-typed variable can reference. */
	lists: PromptListRecord[]
	/**
	 * The PROMPT's type, which decides the variable types on offer — `each` is
	 * code-supplied and belongs only to a system prompt (ADR-0034).
	 */
	promptType: PromptType
	onChange: (next: PromptVariable[]) => void
	/** Keys the body references but no variable declares — highlighted as errors. */
	undeclared?: string[]
	/** Keys declared but never used — highlighted as warnings. */
	unused?: string[]
	isDisabled?: boolean
}

const blank = (): PromptVariable => ({
	key: '',
	label: '',
	type: PromptVariableType.STRING,
	required: false,
})

/**
 * Typed-variable editor, shown beside the template body.
 *
 * Lint state is passed in rather than computed here so the panel and the body editor
 * agree on it — a variable flagged unused here is the same one the save-time lint
 * reports.
 */
export function PromptVariablePanel({
	variables,
	lists,
	promptType,
	onChange,
	undeclared = [],
	unused = [],
	isDisabled = false,
}: PromptVariablePanelProps) {
	const patch = (index: number, next: Partial<PromptVariable>) => {
		onChange(variables.map((v, i) => (i === index ? { ...v, ...next } : v)))
	}

	/**
	 * Move one variable within the list.
	 *
	 * Declaration order is the order a form offers the fields AND the order the
	 * prompt renders them, so this is the control an author uses to make a form read
	 * the way a person fills it. Composed text has its own sequence in `joins`, which
	 * this deliberately does not touch.
	 */
	const move = (index: number, delta: number) => {
		const target = index + delta
		if (target < 0 || target >= variables.length) return

		const next = [...variables]
		const [moved] = next.splice(index, 1)
		next.splice(target, 0, moved as PromptVariable)
		onChange(next)
	}

	return (
		<CardShell as="section" aria-label="Variables" className="p-4">
			<header className="mb-3 flex items-center justify-between">
				<h3 className="text-ds-sm font-medium text-ds-text">Variables</h3>
				<Button size="sm" variant="secondary" isDisabled={isDisabled} onPress={() => onChange([...variables, blank()])}>
					Add variable
				</Button>
			</header>

			{undeclared.length > 0 ? (
				<p role="alert" className="mb-3 text-ds-sm text-ds-danger">
					The body references {undeclared.map((k) => `{{${k}}}`).join(', ')} with no matching variable.
				</p>
			) : null}

			{variables.length === 0 ? (
				<p className="rounded-ds-card border border-dashed border-ds-border p-6 text-center text-ds-sm text-ds-text-muted">
					No variables yet. A prompt with no placeholders renders as fixed text.
				</p>
			) : (
				<ul className="space-y-3">
					{variables.map((variable, index) => (
						<VariableRow
							key={index}
							variable={variable}
							labelPrefix={`Variable ${index + 1}`}
							lists={lists}
							siblings={variables.filter((v, i) => i !== index && v.key !== '')}
							promptType={promptType}
							isDisabled={isDisabled}
							isUnused={unused.includes(variable.key)}
							onPatch={(next) => patch(index, next)}
							onRemove={() => onChange(variables.filter((_, i) => i !== index))}
							onMoveUp={index === 0 ? undefined : () => move(index, -1)}
							onMoveDown={index === variables.length - 1 ? undefined : () => move(index, 1)}
						/>
					))}
				</ul>
			)}
		</CardShell>
	)
}

/**
 * One variable's row.
 *
 * Extracted because the panel and the row are separate concerns: the panel owns the
 * collection, the row owns one variable's controls.
 */
function VariableRow({
	variable,
	labelPrefix,
	lists,
	siblings,
	promptType,
	isDisabled,
	isUnused,
	onPatch,
	onRemove,
	onMoveUp,
	onMoveDown,
}: {
	variable: PromptVariable
	labelPrefix: string
	lists: PromptListRecord[]
	/** Every OTHER variable, so a `join` can name the ones it composes. */
	siblings: PromptVariable[]
	promptType: PromptType
	isDisabled: boolean
	isUnused: boolean
	onPatch: (next: Partial<PromptVariable>) => void
	onRemove: () => void
	/** Absent at the ends of the list, which is what disables the control. */
	onMoveUp?: () => void
	onMoveDown?: () => void
}) {
	return (
		<li className="rounded-ds-md border border-ds-border p-3">
			<div className="grid gap-2 sm:grid-cols-2">
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Key</span>
					<Input
						value={variable.key}
						aria-label={`${labelPrefix} key`}
						disabled={isDisabled}
						onChange={(e) => onPatch({ key: e.target.value })}
					/>
				</label>
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Label</span>
					<Input
						value={variable.label}
						aria-label={`${labelPrefix} label`}
						disabled={isDisabled}
						onChange={(e) => onPatch({ label: e.target.value })}
					/>
				</label>
			</div>

			<div className="mt-2 grid gap-2 sm:grid-cols-2">
				<SelectField
					label="Type"
					ariaLabel={`${labelPrefix} type`}
					value={variable.type}
					isDisabled={isDisabled}
					// A variable always has a type, so there is no empty option to offer.
					isClearable={false}
					options={variableTypesFor(promptType).map((t) => ({ value: t, label: PROMPT_VARIABLE_TYPE_LABEL[t] }))}
					onChange={(value) => onPatch({ type: value as PromptVariableType })}
				/>

				<TypeSpecificControls
					variable={variable}
					labelPrefix={labelPrefix}
					lists={lists}
					siblings={siblings}
					isDisabled={isDisabled}
					onPatch={onPatch}
				/>
			</div>

			<VariableRowActions
				variable={variable}
				labelPrefix={labelPrefix}
				isDisabled={isDisabled}
				isUnused={isUnused}
				onPatch={onPatch}
				onRemove={onRemove}
				onMoveUp={onMoveUp}
				onMoveDown={onMoveDown}
			/>
		</li>
	)
}

/**
 * A row's footer: the required switch, the unused hint, and the reorder/remove
 * controls.
 *
 * Split from the row because the row owns one variable's FIELDS while this owns the
 * actions on it — and together they exceeded the size ceiling.
 */
function VariableRowActions({
	variable,
	labelPrefix,
	isDisabled,
	isUnused,
	onPatch,
	onRemove,
	onMoveUp,
	onMoveDown,
}: {
	variable: PromptVariable
	labelPrefix: string
	isDisabled: boolean
	isUnused: boolean
	onPatch: (next: Partial<PromptVariable>) => void
	onRemove: () => void
	onMoveUp?: () => void
	onMoveDown?: () => void
}) {
	return (
		<div className="mt-3 flex items-center justify-between">
			<div className="flex items-center gap-3">
				<Switch isSelected={variable.required} isDisabled={isDisabled} onChange={(required) => onPatch({ required })}>
					Required
				</Switch>
				{isUnused ? (
					<Chip variant="muted" size="sm">
						Not used in body
					</Chip>
				) : null}
			</div>
			<div className="flex items-center gap-1">
				{/*
						Buttons rather than drag-and-drop: reordering is occasional, and a
						keyboard-reachable pair of controls needs no pointer and no custom
						drop-target semantics to be usable.
					*/}
				<Button
					size="sm"
					variant="muted"
					aria-label={`Move ${labelPrefix} earlier`}
					isDisabled={isDisabled || onMoveUp === undefined}
					onPress={() => onMoveUp?.()}
				>
					↑
				</Button>
				<Button
					size="sm"
					variant="muted"
					aria-label={`Move ${labelPrefix} later`}
					isDisabled={isDisabled || onMoveDown === undefined}
					onPress={() => onMoveDown?.()}
				>
					↓
				</Button>
				<Button size="sm" variant="muted" isDisabled={isDisabled} onPress={onRemove}>
					Remove
				</Button>
			</div>
		</div>
	)
}
